/**
 * Owner: Masa Bayu
 * File: page.tsx
 * Date:Time: 2026-10-04 00:25
 * Description: P19 live side-by-side · SPSU Face · Kemelut Identiti · Percikan Ilahi · vs source.
 *
 * Note: qxread is Vite (not Next App Router). This file is mounted by RoutePanel at /p19.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { QxEditorPageFaceSpsu } from '../../profile/qx-editor-page-face';
import type { AssetLibrary, SPSUDocument, SPSUPage } from '../../profile/spsu-types';
import '../p02/p02-live.css';

const EVIDENCE_BASE = '/evidence/pkt-ladder';
const SOURCE_PNG = `${EVIDENCE_BASE}/p19-page-source-full.png`;
const SPSU_JSON = `${EVIDENCE_BASE}/spsu-p19-page.json`;
const ASSETS_MANIFEST = `${EVIDENCE_BASE}/spsu-p19-assets/manifest.json`;

type EvidenceFile = {
  readonly spsu: SPSUDocument;
  readonly counts?: {
    readonly textElements: number;
    readonly imageElements: number;
  };
};

async function loadJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);
  return (await response.json()) as T;
}

/**
 * Live P19 verification: source raster (left) · SPSU live objects (right).
 */
export default function P19LivePage(): ReactElement {
  const [page, setPage] = useState<SPSUPage | null>(null);
  const [assets, setAssets] = useState<AssetLibrary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [evidence, manifest] = await Promise.all([
          loadJson<EvidenceFile>(SPSU_JSON),
          loadJson<AssetLibrary>(ASSETS_MANIFEST),
        ]);
        const nextPage = evidence.spsu.pages[0] ?? null;
        if (!nextPage) throw new Error('SPSU evidence has no page');
        if (cancelled) return;
        setPage(nextPage);
        setAssets(manifest);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div className="p02-live p02-live-error" role="alert">
        ERROR: {error}
      </div>
    );
  }

  if (!page || !assets) {
    return <div className="p02-live p02-live-loading">Loading SPSU P19…</div>;
  }

  const imageCount = page.elements.filter((el) => el.type === 'image').length;
  const textCount = page.elements.filter((el) => el.type === 'text').length;
  const fontCount = Object.values(assets).filter((a) => a.type === 'font').length;

  return (
    <main className="p02-live">
      <header className="p02-live-header">
        <h1>P19 Live Verification · Side-by-Side</h1>
        <p>
          Kiri: Sumber PDF (raster) · Kanan: Live objects (SPSU) · {imageCount} imej · {textCount} teks ·{' '}
          {fontCount} fon · Kemelut Identiti · Percikan Ilahi
        </p>
      </header>

      <div className="p02-live-compare">
        <section className="p02-live-pane" aria-label="Source reference">
          <h2>Sumber · p19-page-source-full.png</h2>
          <div className="p02-live-frame p02-live-frame-source">
            <img
              src={SOURCE_PNG}
              alt="P19 source reference"
              width={page.size.width}
              height={page.size.height}
              draggable={false}
            />
          </div>
        </section>

        <section className="p02-live-pane" aria-label="SPSU live face">
          <h2>Live Face · QxEditorPageFace (SPSU)</h2>
          <div className="p02-live-frame p02-live-frame-face">
            <QxEditorPageFaceSpsu page={page} assets={assets} editable fit />
          </div>
        </section>
      </div>

      <footer className="p02-live-footer">
        <p className="p02-live-status">STATUS: P19 🟡 OPEN · Owner Decision 2026-10-04 00:25 · P20 🔒</p>
        <p className="p02-live-checks">
          Live editable · Kemelut Identiti · Percikan Ilahi · | 1 | · cream · tiada body-as-image
        </p>
      </footer>
    </main>
  );
}
